import { createSlice } from '@reduxjs/toolkit';

import { RootState } from '..';
import {
  adminGetUserById,
  getUserListForDropDownAsAdmin,
} from './AdminUsersApi';

export interface IUserRole {
  _id: string;
  name: 'Admin' | 'Approver' | 'Enterer' | 'Student' | 'Tutor' | 'Guest';
  type: 'admin' | 'user';
  createdAt: string;
  permissions: IUserPermissions;
  isGuest: boolean;
  updatedAt?: string;
}
export interface IBaseUser {
  _id: string;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  avatar?: string;
  createdAt: string;
  updatedAt?: string;
  role: IUserRole;
  lastLogin?: string;
}

// Course
type TAdminCourseView = 'view';
type TAdminCourseCreate = 'create';
type TAdminCourseEdit = 'edit';
type TAdminCourseDelete = 'delete';
type TAdminCoursePublish = 'publish';
type TCourseView = 'view';

type TUserPermissionAdminCourses = Array<
  | TAdminCourseView
  | TAdminCourseCreate
  | TAdminCourseEdit
  | TAdminCourseDelete
  | TAdminCoursePublish
>;

type TUserPermissionCourses = Array<TCourseView>;

// Schedule
type TScheduleView = 'view';
type TAdminScheduleView = 'view';

type TUserPermissionAdminSchedule = Array<TAdminScheduleView>;

type TUserPermissionSchedule = Array<TScheduleView>;

// COURSE TEMPLATE
export type TAdminCourseTemplateCreate = 'create';
export type TAdminCourseTemplateView = 'view';

type TUserPermissionAdminCourseTemplates = Array<
  TAdminCourseTemplateCreate | TAdminCourseTemplateView
>;

export const USER_PERMISSION_INDIVIDUALS: {
  ADMIN_COURSE_VIEW: TAdminCourseView;
  ADMIN_COURSE_CREATE: TAdminCourseCreate;
  ADMIN_COURSE_EDIT: TAdminCourseEdit;
  ADMIN_COURSE_DELETE: TAdminCourseDelete;
  ADMIN_COURSE_PUBLISH: TAdminCoursePublish;
  ADMIN_SCHEDULE_VIEW: TAdminScheduleView;
  ADMIN_COURSE_TEMPLATE_CREATE: TAdminCourseTemplateCreate;
  ADMIN_COURSE_TEMPLATE_VIEW: TAdminCourseTemplateView;
  COURSE_VIEW: TCourseView;
  SCHEDULE_VIEW: TScheduleView;
} = {
  ADMIN_COURSE_VIEW: 'view',
  ADMIN_COURSE_CREATE: 'create',
  ADMIN_COURSE_EDIT: 'edit',
  ADMIN_COURSE_DELETE: 'delete',
  ADMIN_COURSE_PUBLISH: 'publish',
  ADMIN_SCHEDULE_VIEW: 'view',
  ADMIN_COURSE_TEMPLATE_CREATE: 'create',
  ADMIN_COURSE_TEMPLATE_VIEW: 'view',
  COURSE_VIEW: 'view',
  SCHEDULE_VIEW: 'view',
};

export const USER_PERMISSION: {
  ADMIN_COURSE: TUserPermissionAdminCourses;
  ADMIN_SCHEDULE: TUserPermissionAdminSchedule;
  ADMIN_COURSE_TEMPLATE: TUserPermissionAdminCourseTemplates;
  COURSE: TUserPermissionCourses;
  SCHEDULE: TUserPermissionSchedule;
} = {
  ADMIN_COURSE: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_DELETE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_PUBLISH,
  ],
  ADMIN_SCHEDULE: [USER_PERMISSION_INDIVIDUALS.ADMIN_SCHEDULE_VIEW],
  ADMIN_COURSE_TEMPLATE: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_TEMPLATE_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_TEMPLATE_VIEW,
  ],
  COURSE: [USER_PERMISSION_INDIVIDUALS.COURSE_VIEW],
  SCHEDULE: [USER_PERMISSION_INDIVIDUALS.SCHEDULE_VIEW],
};

export interface IUserPermissions {
  quiz?: Array<'view'>;
  submission?: Array<'view' | 'create' | 'edit' | 'delete'>;
  quizHistory?: Array<'view'>;
  course?: TUserPermissionCourses;
  schedule?: TUserPermissionSchedule;
  // ADMIN
  adminSubTopic?: Array<'view' | 'create' | 'edit' | 'delete'>;
  adminQuestion?: Array<'view' | 'create' | 'edit' | 'delete' | 'approve'>;
  adminQuiz?: Array<'view' | 'create' | 'edit' | 'delete' | 'publish'>;
  adminStudent?: Array<'view'>;
  adminCourse?: TUserPermissionAdminCourses;
  adminCourseTemplate?: TUserPermissionAdminCourseTemplates;
  adminSchedule?: TUserPermissionAdminSchedule;
}

export const USER_ROLE_NAMES = {
  ADMIN: 'Admin',
  APPROVER: 'Approver',
  ENTERER: 'Enterer',
  STUDENT: 'Student',
  TUTOR: 'Tutor',
  GUEST: 'Guest',
};

export interface IUserCurriculum {
  curriculum: string;
  grade: string;
  subject: string;
  subjectCode: string;
}

export interface IUser extends IBaseUser {
  isQuiztopiyaPendingTrial?: boolean;
  assessmentEngineDate?: string;
  quiztopiyaTrialExpiration?: string;
  curriculums?: Array<IUserCurriculum>;
  isContinueSubmissionWithTimerEnabled?: boolean;
  isLeadGenSubmitted?: boolean;
  students?: Array<{
    username: string;
  }>;
  tutopiyaUserId?: string;
}

export interface IUsersState {
  status: 'idle' | 'loading' | 'rejected';
  count: number;
  user: IUser | undefined;
  // Admin
  userListDataForAdminForDropDown?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IBaseUser>;
  };
}

const initialState: IUsersState = {
  status: 'idle',
  count: 0,
  user: undefined,
  userListDataForAdminForDropDown: undefined,
};

export const UsersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    resetUserListDataForAdmin: (state) => {
      state.userListDataForAdminForDropDown = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET USER BY ID
      .addCase(adminGetUserById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminGetUserById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.user = action.payload;
      })
      .addCase(adminGetUserById.rejected, (state) => {
        state.status = 'rejected';
      })
      // V2
      // Admin
      // Get user list
      .addCase(getUserListForDropDownAsAdmin.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getUserListForDropDownAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;
        const newUserListDataForAdminForDropDown = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.userListDataForAdminForDropDown = newUserListDataForAdminForDropDown;
      })
      .addCase(getUserListForDropDownAsAdmin.rejected, (state) => {
        state.status = 'rejected';
      });
  },
});

export const getUsersState = (state: RootState): RootState['users'] =>
  state.users;

export const { resetUserListDataForAdmin } = UsersSlice.actions;

export * from './AdminUsersApi';
export default UsersSlice.reducer;
